import { useCallback, useMemo, useState } from 'react';
import {
  Background,
  Controls,
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  useEdgesState,
  useNodesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Link } from 'react-router-dom';

import { Api } from '../lib/api.js';
import { useApi, useWallet } from '../lib/wallet.jsx';
import { Button, Card, Empty, ErrorState, Field, Skeleton } from '../components/ui.jsx';
import { JourneySteps, JourneyNext } from '../components/Journey.jsx';

/**
 * Deploy a workflow.
 *
 * A visual canvas for wiring one or more of your agents into a pipeline: drag
 * an agent onto the canvas, connect its output to the next agent's input, name
 * the pipeline and deploy. Each stage becomes a scheduled workflow bound to its
 * agent, which is the existing, authorized, on-chain-attested workflow engine —
 * this screen is a builder on top of it, not a second execution path.
 *
 * A single agent dropped on the canvas and deployed is the "one click, one
 * agent" case. Two or more connected is the multi-agent case. Both produce real
 * workflow records; nothing here fabricates a pipeline that cannot run.
 */

const nodeStyle = {
  background: '#15111F',
  border: '1px solid #6F5C8D',
  borderRadius: 12,
  color: '#F7F4FF',
  padding: '10px 14px',
  fontSize: 13,
  fontFamily: 'Inter, system-ui, sans-serif',
  minWidth: 150,
};

// Common things people set an agent workflow up to do. Clicking one adds its
// instruction to the workflow's purpose; they can also type their own on top.
// These are plain text prompts, not a second capability system — they become
// the workflow instruction the agent already knows how to run.
const GOAL_PRESETS = [
  { key: 'product', label: 'Build a product with your CTO agent',
    text: 'Work with the CTO agent to develop the product: break the goal into milestones, assign build tasks, review progress and report what shipped and what is blocked.' },
  { key: 'email', label: 'Monitor and triage emails',
    text: 'Monitor the inbox, summarize what came in, flag anything urgent, and draft replies for the important messages.' },
  { key: 'negotiate', label: 'Review and negotiate proposals',
    text: 'Review incoming proposals, assess terms against our goals, and negotiate toward a concrete next step. Never accept or send anything without surfacing the tradeoffs first.' },
  { key: 'research', label: 'Research market and competitors',
    text: 'Research the current market and competitor moves, and report what is actually happening with source URLs. Lead with the takeaway.' },
  { key: 'outreach', label: 'Draft partner and investor outreach',
    text: 'Draft business development outreach: open conversations naturally, qualify fit, and move each relationship toward a concrete next step.' },
  { key: 'report', label: 'Summarize progress and flag blockers',
    text: 'Pull together progress across active workstreams, quantify where possible, and flag the blockers that need a decision.' },
];

/** Build the workflow instruction from the picked presets plus any free text. */
function composePurpose(selectedKeys, freeText) {
  const parts = GOAL_PRESETS.filter((g) => selectedKeys.includes(g.key)).map((g) => g.text);
  const extra = freeText.trim();
  if (extra) parts.push(extra);
  return parts.join('\n\n');
}

function Inner({ pushToast }) {
  const { sessionReady, isAuthenticated, promptConnect, sendIntent } = useWallet();
  const { data, loading, error, reload } = useApi(() => Api.agents(), [sessionReady], { skip: !sessionReady });

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [name, setName] = useState('');
  const [schedule, setSchedule] = useState('0 9 * * *');
  const [deploying, setDeploying] = useState(false);
  const [deployed, setDeployed] = useState(false);
  const [seq, setSeq] = useState(1);
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [purposeText, setPurposeText] = useState('');

  const toggleGoal = useCallback((key) => {
    setSelectedGoals((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }, []);

  const purpose = useMemo(() => composePurpose(selectedGoals, purposeText), [selectedGoals, purposeText]);

  const onConnect = useCallback((params) => setEdges((eds) => addEdge({ ...params, animated: true }, eds)), [setEdges]);

  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC', []);

  const addAgentNode = useCallback(
    (agent) => {
      const id = `stage-${seq}`;
      setSeq((n) => n + 1);
      setNodes((nds) => [
        ...nds,
        {
          id,
          data: { label: agent.name, agentId: agent.id, role: agent.role, minted: Boolean(agent.passportTokenId) },
          position: { x: 120 + (nds.length % 3) * 220, y: 80 + Math.floor(nds.length / 3) * 130 },
          style: nodeStyle,
        },
      ]);
    },
    [seq, setNodes],
  );

  const deploy = useCallback(async () => {
    if (!isAuthenticated) {
      promptConnect();
      return;
    }
    if (nodes.length === 0) {
      pushToast?.({ tone: 'danger', message: 'Add at least one agent to the canvas.' });
      return;
    }
    if (!name.trim()) {
      pushToast?.({ tone: 'danger', message: 'Give the workflow a name.' });
      return;
    }
    setDeploying(true);
    try {
      // Order stages by their wiring: a node with no incoming edge is a start,
      // then follow edges. Any unwired nodes still deploy as their own stage.
      const ordered = orderStages(nodes, edges);
      let created = 0;
      for (let i = 0; i < ordered.length; i += 1) {
        const stage = ordered[i];
        const stageName = ordered.length > 1 ? `${name.trim()} · ${i + 1}. ${stage.data.label}` : name.trim();
        await Api.createWorkflow({
          agentId: stage.data.agentId,
          name: stageName.slice(0, 200),
          schedule,
          timezone,
          instruction: purpose || stage.data.role || undefined,
          maxRunsPerDay: 1,
        });
        created += 1;
      }
      // Record the workflow generation on chain once for the whole deploy.
      // Wallet-scoped (no passport required), so it fires MetaMask from tap one
      // for any signed-in wallet. The DB workflows are already saved, so a
      // rejected signature never loses them.
      let chainNote = '';
      try {
        const { intent } = await Api.walletWorkflowIntent();
        const r = await sendIntent({ intent, txType: 'wallet_workflow' });
        if (r.status === 'success') chainNote = ' Recorded on chain.';
      } catch (err) {
        chainNote = ` On-chain record failed: ${err.message}`;
      }
      setDeployed(true);
      pushToast?.({
        tone: 'success',
        message:
          `${created > 1 ? `Pipeline created as ${created} linked workflows.` : 'Workflow created.'}` +
          chainNote,
      });
      setNodes([]);
      setEdges([]);
      setName('');
      setSelectedGoals([]);
      setPurposeText('');
    } catch (err) {
      pushToast?.({ tone: 'danger', message: err.message });
    } finally {
      setDeploying(false);
    }
  }, [isAuthenticated, promptConnect, nodes, edges, name, schedule, timezone, purpose, pushToast, sendIntent, setNodes, setEdges]);

  if (error) return <ErrorState error={error} onRetry={reload} context="Your agents could not be loaded" />;
  if (!sessionReady || loading || !data) return <Skeleton rows={5} />;

  const agents = (data.agents ?? []).filter((a) => a.status !== 'archived');

  return (
    <div className="stack wfbuilder">
      <JourneySteps current={2} />
      <header className="page-header">
        <div>
          <h1 className="page-title">Deploy a workflow</h1>
          <p className="page-subtitle">
            Drag your agents onto the canvas and connect them into a pipeline. One agent or several — deploy it and each
            stage becomes a scheduled, authorizable workflow.
          </p>
        </div>
      </header>

      {agents.length === 0 ? (
        <Empty
          title="No agents to wire yet"
          description="Create an agent first, then come back to compose it into a workflow."
        />
      ) : deployed ? (
        <Card title="Workflow created">
          <p className="text-muted">
            It is saved to your account as a draft. Authorise it on the Workflows page to start its scheduled runs.
          </p>
          <div className="wfbuilder__deploy">
            <Link className="button button--primary" to="/workflows">Authorise on Workflows</Link>
            <Button variant="secondary" onClick={() => setDeployed(false)}>Build another</Button>
          </div>
        </Card>
      ) : (
        <>
          <section className="wfbuilder__purpose">
            <div className="wfbuilder__purpose-head">
              <h2 className="wfbuilder__section-title">What should this workflow do?</h2>
              <p className="wfbuilder__section-hint">
                Pick one or more goals and add your own detail. This becomes the standing instruction the agents run on
                each scheduled cycle — for example, the CEO agent coordinating the CTO agent to build a product,
                monitoring email, or negotiating proposals.
              </p>
            </div>
            <div className="wfbuilder__goals">
              {GOAL_PRESETS.map((g) => {
                const on = selectedGoals.includes(g.key);
                return (
                  <button
                    key={g.key}
                    type="button"
                    className={`wfbuilder__goal${on ? ' wfbuilder__goal--on' : ''}`}
                    aria-pressed={on}
                    onClick={() => toggleGoal(g.key)}
                  >
                    <span className="wfbuilder__goal-check" aria-hidden="true">{on ? '✓' : '+'}</span>
                    <span className="wfbuilder__goal-label">{g.label}</span>
                  </button>
                );
              })}
            </div>
            <Field
              label="Add your own instructions"
              htmlFor="wf-purpose"
              hint="Type anything specific. Combined with the goals you picked above."
            >
              <textarea
                id="wf-purpose"
                className="input wfbuilder__purpose-input"
                rows={4}
                value={purposeText}
                maxLength={4000}
                placeholder="e.g. Focus on the NeuroMesh launch. Loop in the CTO agent daily, escalate anything that blocks the demo."
                onChange={(e) => setPurposeText(e.target.value)}
              />
            </Field>
            {purpose && (
              <div className="wfbuilder__purpose-preview">
                <span className="wfbuilder__section-hint">This workflow's purpose</span>
                <p className="wfbuilder__purpose-preview-text">{purpose}</p>
              </div>
            )}
          </section>

          <section className="wfbuilder__palette">
            <span className="wfbuilder__palette-label">Your agents</span>
            <div className="wfbuilder__chips">
              {agents.map((a) => (
                <button key={a.id} type="button" className="wfbuilder__chip" onClick={() => addAgentNode(a)}>
                  + {a.name}
                </button>
              ))}
            </div>
          </section>

          <section className="wfbuilder__canvas">
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              fitView
              proOptions={{ hideAttribution: true }}
              style={{ background: '#08060F' }}
            >
              <Background color="#2A2336" gap={20} />
              <Controls />
            </ReactFlow>
          </section>

          <section className="wfbuilder__config">
            <Field label="Workflow name" htmlFor="wf-name" hint="How this pipeline appears on your Workflows screen.">
              <input id="wf-name" className="input" value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Schedule (cron)" htmlFor="wf-cron" hint={`Runs on this schedule in ${timezone}. Default is 9am daily.`}>
              <input id="wf-cron" className="input mono" value={schedule} maxLength={120} onChange={(e) => setSchedule(e.target.value)} />
            </Field>
            <div className="wfbuilder__deploy">
              <span className="text-muted">{nodes.length} stage{nodes.length === 1 ? '' : 's'}{edges.length ? ` · ${edges.length} link${edges.length === 1 ? '' : 's'}` : ''}</span>
              <Button variant="primary" loading={deploying} onClick={deploy} disabled={nodes.length === 0}>
                {isAuthenticated ? 'Deploy workflow' : 'Connect wallet to deploy'}
              </Button>
            </div>
            {!isAuthenticated && (
              <p className="text-muted">
                Building the pipeline needs nothing. Deploying it creates real workflows on your account, which needs a
                connected wallet. You authorize each on chain from the Workflows screen.
              </p>
            )}
          </section>
        </>
      )}

      <JourneyNext current={2} done={deployed} />
    </div>
  );
}

/** Topologically order stages by edges; unwired nodes keep insertion order. */
function orderStages(nodes, edges) {
  if (nodes.length <= 1 || edges.length === 0) return nodes;
  const incoming = new Map(nodes.map((n) => [n.id, 0]));
  const next = new Map(nodes.map((n) => [n.id, []]));
  for (const e of edges) {
    if (incoming.has(e.target)) incoming.set(e.target, incoming.get(e.target) + 1);
    if (next.has(e.source)) next.get(e.source).push(e.target);
  }
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const queue = nodes.filter((n) => (incoming.get(n.id) ?? 0) === 0).map((n) => n.id);
  const ordered = [];
  const visited = new Set();
  while (queue.length) {
    const id = queue.shift();
    if (visited.has(id)) continue;
    visited.add(id);
    ordered.push(byId.get(id));
    for (const t of next.get(id) ?? []) {
      incoming.set(t, incoming.get(t) - 1);
      if (incoming.get(t) === 0) queue.push(t);
    }
  }
  // Any node caught in a cycle still gets deployed.
  for (const n of nodes) if (!visited.has(n.id)) ordered.push(n);
  return ordered;
}

export function WorkflowBuilder({ pushToast }) {
  return (
    <ReactFlowProvider>
      <Inner pushToast={pushToast} />
    </ReactFlowProvider>
  );
}
